/**
 * StockLocations — Depo / lokasyon yönetimi (CRUD).
 *
 * Mal kabul, stok transferi ve sayım ekranları buradaki lokasyonları kullanır.
 * Backend hiyerarşiktir (parent): "Ana Depo > Buzdolabı" gibi. Tip alanı
 * (Depo/Mutfak/Bar/Buzdolabı) raporlama ve filtreleme içindir.
 */
import { ActionIcon, Badge, Button, Card, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useResource } from "../../../shared/api";
import { useRemove } from "../../../shared/api/resources";
import { EmptyState, PageHeader, ResourceForm, confirmDelete, type FormField } from "../../../shared/ui";

const TYPE_OPTIONS = [
  { value: "WAREHOUSE", label: "Depo" },
  { value: "KITCHEN", label: "Mutfak" },
  { value: "BAR", label: "Bar" },
  { value: "FRIDGE", label: "Buzdolabı" },
];
const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
const TYPE_COLOR: Record<string, string> = {
  WAREHOUSE: "blue",
  KITCHEN: "orange",
  BAR: "grape",
  FRIDGE: "cyan",
};

export function StockLocationsPage() {
  const { data, isLoading } = useResource<any>("stock-locations");
  const remove = useRemove("stock-locations");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const locations: any[] = data?.results || data || [];
  const nameById = new Map<string, string>(locations.map((l) => [l.id, l.name]));

  const fields: FormField[] = [
    { name: "name", label: "Ad", required: true, placeholder: "Ana Depo, Bar Buzdolabı, …" },
    {
      name: "location_type",
      label: "Tip",
      type: "select",
      options: TYPE_OPTIONS,
      defaultValue: "WAREHOUSE",
    },
    {
      name: "parent",
      label: "Üst Lokasyon (opsiyonel)",
      type: "select",
      // Kendisi hariç diğer lokasyonlar — döngüsel hiyerarşiyi önlemek için
      // düzenlenen kayıt listeden çıkarılır.
      options: locations
        .filter((l) => l.id !== editing?.id)
        .map((l) => ({ value: l.id, label: l.name })),
      clearable: true,
      placeholder: "Bağlı olduğu üst depo (örn. Ana Depo)",
      description: "Hiyerarşi için — boş bırakılırsa kök lokasyon olur.",
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
  ];

  async function handleDelete(l: any) {
    if (!(await confirmDelete(`"${l.name}" lokasyonu silinsin mi?`))) return;
    try {
      await remove.mutateAsync(l.id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail
          || "Silinemedi — bu lokasyonda stok veya alt lokasyon olabilir.",
        color: "red",
      });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Depolar"
        subtitle="Stok lokasyonlarını yönetin — mal kabul ve transfer bu lokasyonları kullanır"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Depo
          </Button>
        }
      />

      {isLoading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : locations.length === 0 ? (
        <EmptyState
          title="Henüz depo yok"
          description="Mal kabul ve stok takibi için en az bir depo/lokasyon ekleyin."
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Üst Lokasyon</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {locations.map((l) => (
                <Table.Tr key={l.id}>
                  <Table.Td fw={500}>{l.name}</Table.Td>
                  <Table.Td>
                    <Badge color={TYPE_COLOR[l.location_type] || "gray"} variant="light">
                      {TYPE_LABEL[l.location_type] || l.location_type}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {l.parent
                      ? <Text size="sm">{nameById.get(l.parent) || "—"}</Text>
                      : <Text size="sm" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    {l.is_active
                      ? <Badge color="green">Aktif</Badge>
                      : <Badge color="gray">Pasif</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => { setEditing(l); open(); }}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(l)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="stock-locations"
        title={editing ? "Depo Düzenle" : "Yeni Depo"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
